/**
 * TRUSTGATE_LOCAL_AUTH_INITIALIZATION_GUARD_V1
 * 
 * Fail-closed guard for TrustGate Firebase Auth initialization.
 * Verifies Firebase config exists, app initializes, and auth is available.
 * Provides clear developer-facing error messages for local dev.
 */

import { auth, firebaseApp } from '../../firebase/init';

export interface AuthInitializationResult {
  success: boolean;
  configMissing: boolean;
  appInitialized: boolean;
  authAvailable: boolean;
  missingKeys?: string[];
  error?: string;
}

/**
 * Verify Firebase Auth initialization for TrustGate local dev.
 * Returns detailed failure information for developer remediation.
 */
export function verifyAuthInitialization(): AuthInitializationResult {
  const result: AuthInitializationResult = {
    success: true,
    configMissing: false,
    appInitialized: false,
    authAvailable: false,
  };

  // Check Firebase app config
  try {
    const appOptions = firebaseApp?.options;
    if (!appOptions) {
      result.success = false;
      result.configMissing = true;
      result.error = 'Firebase app not initialized';
      return result;
    }

    // Check required config keys
    const requiredKeys: Array<keyof typeof appOptions> = ['apiKey', 'authDomain', 'projectId', 'appId'];
    const missingKeys: string[] = [];
    
    for (const key of requiredKeys) {
      if (!appOptions[key]) {
        missingKeys.push(key);
      }
    }

    if (missingKeys.length > 0) {
      result.success = false;
      result.configMissing = true;
      result.missingKeys = missingKeys;
      result.error = `Missing Firebase config keys: ${missingKeys.join(', ')}`;
      return result;
    }

    result.appInitialized = true;
  } catch (error) {
    result.success = false;
    result.configMissing = true;
    result.error = error instanceof Error ? error.message : 'Unknown error checking Firebase config';
    return result;
  }

  // Check Auth instance
  try {
    if (!auth) {
      result.success = false;
      result.authAvailable = false;
      result.error = 'Firebase Auth instance is not available';
      return result;
    }

    result.authAvailable = true;
  } catch (error) {
    result.success = false;
    result.authAvailable = false;
    result.error = error instanceof Error ? error.message : 'Unknown error checking Auth instance';
    return result;
  }

  return result;
}

/**
 * Get developer-facing error message for local dev.
 * Includes expected env/config key names and Sotto command.
 */
export function getAuthErrorMessage(result: AuthInitializationResult): string {
  if (result.success) {
    return '';
  }

  const lines: string[] = [
    'Firebase Auth is not initialized for TrustGate local dev.',
    '',
  ];

  if (result.configMissing) {
    lines.push('Missing Firebase configuration:');
    lines.push('');
    lines.push('Required environment variables:');
    lines.push('  - VITE_FIREBASE_API_KEY');
    lines.push('  - VITE_FIREBASE_AUTH_DOMAIN');
    lines.push('  - VITE_FIREBASE_PROJECT_ID');
    lines.push('  - VITE_FIREBASE_STORAGE_BUCKET');
    lines.push('  - VITE_FIREBASE_MESSAGING_SENDER_ID');
    lines.push('  - VITE_FIREBASE_APP_ID');
    lines.push('  - VITE_FIREBASE_MEASUREMENT_ID');
    lines.push('');

    if (result.missingKeys && result.missingKeys.length > 0) {
      lines.push(`Specifically missing: ${result.missingKeys.join(', ')}`);
      lines.push('');
    }

    lines.push('To fix:');
    lines.push('  1. Create or update .env file with the above variables');
    lines.push('  2. Restart the dev server');
    lines.push('');
  }

  if (!result.authAvailable) {
    lines.push('Firebase Auth initialization failed.');
    lines.push('');
    lines.push('To fix:');
    lines.push('  1. Verify Firebase project configuration is correct');
    lines.push('  2. Check browser console for detailed error messages');
    lines.push('');
  }

  lines.push('Note: Sotto is accessible via explicit command only:');
  lines.push('  npm run dev:sotto');
  lines.push('');

  if (result.error) {
    lines.push(`Error details: ${result.error}`);
  }

  return lines.join('\n');
}

/**
 * Log auth initialization status to console (safe for local dev).
 */
export function logAuthInitializationStatus(): void {
  const result = verifyAuthInitialization();
  
  if (result.success) {
    console.log('%c✓ TrustGate Firebase Auth initialized successfully', 'color:#10b981;font-weight:700');
  } else {
    console.error('%c✗ TrustGate Firebase Auth initialization failed', 'color:#ef4444;font-weight:700');
    console.error(getAuthErrorMessage(result));
  }
}
